import { isRecommendationRequestRejected } from './recommendationError';
import { PRODUCT_QUERY_KEYS } from './keys';

import type {
  ProductsAllRequest,
  ProductsAllResponse,
  ProductsCollectionRequest,
  DiscountCampaignProductsRequest,
  ProductsCollectionResponse,
  ProductsByIdsRequest,
  ProductsByIdsResponse,
  ProductsBySkusRequest,
  ProductsBySkusResponse,
  TopRankedProductsRequest,
  TopRankedProductsResponse,
  ProductsCollectionCompetitorRequest,
  ProductsCollectionCompetitorResponse,
  ProductTaxonomy,
  ProductBrandTaxonomy,
  CategoryTaxonomy,
  ProductBrowseRequest,
  ProductBrowseResponse,
  ProductsDetailRequest,
  ProductsDetailResponse,
  ProductMetaRequest,
  ProductMetaResponse,
  ProductIngredientsRequest,
  ProductIngredientsResponse,
  ProductSearchRequest,
  ProductSearchResponse,
  ListRecommendationsRequest,
  ListRecommendationsResponse,
  AIInsightRequest,
  AIInsightResponse,
  QueryListRecommendationsRequest,
  ProductsRecommendRequest,
  ProductsRecommendResponse,
  RoutineRecommendationsRequest,
  RoutineRecommendationsResponse,
} from './types';
import { PAGE_QUERIES } from '../../page/utils/queries';
import { productAPIClient } from '../productAPIClient';
import { infiniteQueryOptions, mutationOptions, queryOptions } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

/**
 * An ingredient list rarely changes (the endpoint sends `Cache-Control: max-age=86400`), so a cached
 * list stays fresh for a day. This sets freshness only: how long an unused list stays cached is
 * still the client's `gcTime`, the same as the rest of the product data shown beside it.
 */
const PRODUCT_INGREDIENTS_STALE_TIME_MS = 24 * 60 * 60 * 1000;

export const PRODUCT_QUERIES = {
  getProductsAll: (params?: ProductsAllRequest) =>
    queryOptions<ProductsAllResponse>({
      queryKey: PRODUCT_QUERY_KEYS.all(params),
      queryFn: () => productAPIClient.getProductsAll(params),
    }),
  getProductsCollections: (params?: ProductsCollectionRequest) =>
    queryOptions<ProductsCollectionResponse>({
      queryKey: PRODUCT_QUERY_KEYS.collections(params),
      queryFn: () => productAPIClient.getProductsCollections(params),
      // The endpoint matches on the term name, so without one the request can only come back empty.
      enabled: Boolean(params?.collectionName),
    }),
  /**
   * Pages through a flash-sale campaign. The slug resolves to a term id through the cached
   * collection-detail query, so later pages only pay for `/products/browse`.
   */
  getDiscountCampaignProductsInfinite: (params: DiscountCampaignProductsRequest, queryClient: QueryClient) =>
    infiniteQueryOptions({
      queryKey: PRODUCT_QUERY_KEYS.discountCampaign(params),
      enabled: Boolean(params.collectionSlug?.trim()),
      initialPageParam: 1,
      queryFn: async ({ pageParam }): Promise<ProductsCollectionResponse> => {
        const { collectionSlug, ...browseParams } = params;
        if (!collectionSlug?.trim()) {
          return { products: [], total: 0, page: pageParam, limit: params.limit };
        }
        const detail = await queryClient.ensureQueryData(PAGE_QUERIES.getCollectionDetail(collectionSlug));
        const collectionId = detail?.term?.termId;
        if (!Number.isInteger(collectionId) || collectionId <= 0 || detail.term.slug !== collectionSlug) {
          throw new Error('Campaign collection could not be resolved');
        }
        return productAPIClient.getProductBrowse({
          ...browseParams,
          page: pageParam,
          campaignCollections: String(collectionId),
          sortBy: 'discount',
          order: 'desc',
        });
      },
      // Counts unique products: discount order can shift between requests and repeat one across
      // pages, and counting the repeat would stop paging before every product has loaded.
      getNextPageParam: (lastPage, pages, lastPageParam) => {
        const loadedCount = new Set(pages.flatMap((page) => page.products.map((product) => product.productId))).size;
        return lastPage.products.length > 0 && loadedCount < lastPage.total ? lastPageParam + 1 : undefined;
      },
    }),
  getProductsByIds: (params: ProductsByIdsRequest) =>
    queryOptions<ProductsByIdsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.byIds(params),
      queryFn: () => productAPIClient.getProductsByIds(params),
      enabled: params.ids.length > 0,
    }),
  getProductsBySkus: (params: ProductsBySkusRequest) =>
    queryOptions<ProductsBySkusResponse>({
      queryKey: PRODUCT_QUERY_KEYS.bySkus(params),
      queryFn: () => productAPIClient.getProductsBySkus(params),
      // Only honoured for `useQuery`; suspense and RSC prefetch call sites
      // must gate on an empty SKU list themselves.
      enabled: params.skus.length > 0,
    }),
  getProductsDetail: (params: ProductsDetailRequest) =>
    queryOptions<ProductsDetailResponse>({
      queryKey: PRODUCT_QUERY_KEYS.detail(params),
      queryFn: () => productAPIClient.getProductsDetail(params),
      enabled: Boolean(params.slug),
    }),
  getProductMeta: (params: ProductMetaRequest) =>
    queryOptions<ProductMetaResponse>({
      queryKey: PRODUCT_QUERY_KEYS.byIdMeta(params),
      queryFn: () => productAPIClient.getProductMeta(params),
      enabled: Boolean(params.productId),
    }),
  getProductIngredients: (params: ProductIngredientsRequest) =>
    queryOptions<ProductIngredientsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.byIdIngredients(params),
      queryFn: () => productAPIClient.getProductIngredients(params),
      enabled: Boolean(params.productId),
      staleTime: PRODUCT_INGREDIENTS_STALE_TIME_MS,
    }),
  getTopRankedProducts: (params?: TopRankedProductsRequest) =>
    queryOptions<TopRankedProductsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.topRanked(params),
      queryFn: () => productAPIClient.getTopRankedProducts(params),
    }),
  getProductTaxonomy: () =>
    queryOptions<ProductTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.taxonomy(),
      queryFn: () => productAPIClient.getProductTaxonomy(),
    }),
  getProductBrandTaxonomy: () =>
    queryOptions<ProductBrandTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.brandTaxonomy(),
      queryFn: () => productAPIClient.getProductBrandTaxonomy(),
    }),
  getProductsCollectionCompetitor: (params: ProductsCollectionCompetitorRequest) =>
    queryOptions<ProductsCollectionCompetitorResponse>({
      queryKey: PRODUCT_QUERY_KEYS.competitorCollections(params),
      queryFn: () => productAPIClient.getProductsCollectionCompetitor(params),
    }),
  getCategoryTaxonomy: () =>
    queryOptions<CategoryTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.categoryTaxonomy(),
      queryFn: () => productAPIClient.getCategoryTaxonomy(),
    }),
  getAllCategoryTaxonomy: () =>
    queryOptions<CategoryTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.allCategoryTaxonomy(),
      queryFn: () => productAPIClient.getAllCategoryTaxonomy(),
    }),
  getProductBrowse: (params?: ProductBrowseRequest) =>
    queryOptions<ProductBrowseResponse>({
      queryKey: PRODUCT_QUERY_KEYS.browse(params),
      queryFn: () => productAPIClient.getProductBrowse(params),
    }),
  getProductSearch: (params: ProductSearchRequest) =>
    queryOptions<ProductSearchResponse>({
      queryKey: PRODUCT_QUERY_KEYS.search(params),
      queryFn: () => productAPIClient.getProductSearch(params),
    }),
  getListRecommendations: (params: QueryListRecommendationsRequest) =>
    queryOptions<ListRecommendationsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.recommendations(params),
      queryFn: () => productAPIClient.getListRecommendations(params as ListRecommendationsRequest),
      enabled: Boolean(params?.productId),
    }),
  getAIInsight: (params: AIInsightRequest) =>
    queryOptions<AIInsightResponse>({
      queryKey: PRODUCT_QUERY_KEYS.aiInsight(params),
      queryFn: () => productAPIClient.getAIInsight(params),
      enabled: Boolean(params.orgProductId && params.altProductId),
    }),
  getRecommendedProducts: (params: ProductsRecommendRequest) =>
    queryOptions<ProductsRecommendResponse>({
      queryKey: PRODUCT_QUERY_KEYS.recommend(params),
      queryFn: () => productAPIClient.getRecommendedProducts(params),
      retry: (failureCount, error) => !isRecommendationRequestRejected(error) && failureCount < 3,
      refetchOnWindowFocus: (query) => !isRecommendationRequestRejected(query.state.error),
    }),
  getRoutineRecommendations: (params: RoutineRecommendationsRequest) =>
    queryOptions<RoutineRecommendationsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.routineRecommendations(params),
      queryFn: () => productAPIClient.getRoutineRecommendations(params),
      enabled: Boolean(params.productId),
    }),
};

export const PRODUCT_MUTATIONS = {
  getProductsByIds: () => {
    return mutationOptions({
      mutationFn: productAPIClient.getProductsByIds,
    });
  },
  getAIInsight: () => {
    return mutationOptions({
      mutationFn: productAPIClient.getAIInsight,
    });
  },
};
